import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Row, Form, Tooltip, Col } from 'antd';

import '../Clients/Clients.scss';
import { RootState } from '../../state/store';
import Icon from '../../common/components/Icon/Icon';
import { getUserSession } from '../../utils/userSession';
import { validatePasswordPolicy } from '../../utils/validations';
import { LrvForm } from '../../common/components/LrvForm/LrvForm';
import { useFormErrorFields } from '../../hooks/useFormErrorFields';
import { LrvModal } from '../../common/components/LrvModal/LrvModal';
import PasswordPolicy from '../../common/components/LrvPopover/PasswordPolicy';
import { LrvPasswordV2 } from '../../common/components/LrvPasswordV2/LrvPasswordV2';

import * as usersSlice from './usersSlice';
import styles from './ResetPasswordForm.module.scss';

export const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const { errorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const [userSessionPassword, setUserSessionPassword] = useState('');
  const [userTargetNewPassword, setUserTargetNewPassword] = useState('');
  const [disabledButtonFormResetPassword, setDisabledButtonFormResetPassword] = useState(true);

  const {
    selectedUser,
    isResettingPassword,
    hasNewPasswordError,
    passwordResetErrorKey,
    hasCurrentPasswordError,
    canCloseResettingPasswordModal,
    showResetPasswordModal,
  } = useSelector((state: RootState) => state.users);
  
  const userSession = getUserSession();

  useEffect(() => {
    if (canCloseResettingPasswordModal && !isResettingPassword) {
      dispatch(usersSlice.setShowResetPasswordModal(false));
      setDisabledButtonFormResetPassword(true);

      form.setFieldsValue({
        currentPassword: undefined,
        newPassword: undefined,
      });

      setUserSessionPassword('');
      setUserTargetNewPassword('');
    }
  }, [canCloseResettingPasswordModal, form, isResettingPassword]);

  function generateRandomPassword () {
    let randomPassword = '';
    const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const initialIndex = 0;

    randomPassword += upperCase[getRandomInteger(initialIndex, upperCase.length - 1)];
    randomPassword += upperCase[getRandomInteger(initialIndex, upperCase.length - 1)];
    randomPassword += lowerCase[getRandomInteger(initialIndex, lowerCase.length - 1)];
    randomPassword += lowerCase[getRandomInteger(initialIndex, lowerCase.length - 1)];
    randomPassword += numbers[getRandomInteger(initialIndex, numbers.length - 1)];
    randomPassword += numbers[getRandomInteger(initialIndex, numbers.length - 1)];
    return randomPassword;
  }

  const getRandomInteger = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  function renderGeneratePasswordButton () {
    return <Tooltip trigger='click' title={t('users.passwordCopied')}>
      <Button
        id='generate_password_button'
        htmlType='button'
        className={styles.btnGeneratePassword}
        onClick={() => {
          const randomPassword = generateRandomPassword();
          setUserTargetNewPassword(randomPassword);
          form.setFieldsValue({
            newPassword: randomPassword,
          });

          setDisabledButtonFormResetPassword(
            userSessionPassword === '' ||
            form.getFieldsError().filter(({ errors }) => errors.length).length > 0
          );

          if (navigator.clipboard) {
            navigator.clipboard.writeText(randomPassword);
          }
        }}
      >{t('users.generatePassword')}</Button>
    </Tooltip>;
  }

  const renderCurrentPassword = () => {
    const hasError = errorFields.includes('currentPassword');

    return (
      <Form.Item
        className={styles.label}
        name='currentPassword'
        validateStatus={hasCurrentPasswordError ? 'error' : undefined}
        help={['incorrectPassword'].includes(passwordResetErrorKey) ? t(`users.resetPasswordErrors.${passwordResetErrorKey}`) : null}
        rules={[{ required: true, message: t('users.passwordRequired') }]}
      >
        <LrvPasswordV2
          label={t('users.yourCurrentPassword', { name: userSession.firstName + ' ' + userSession.lastName })}
          theme='light'
          autoFocus
          id='currentPassword'
          autoComplete='off'
          value={userSessionPassword}
          hasError={hasError}
          onChange={(e) => setUserSessionPassword(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderNewPassword = () => {
    const hasError = errorFields.includes('newPassword');

    return (
      <Form.Item
        name='newPassword'
        validateStatus={hasNewPasswordError ? 'error' : undefined}
        help={['passwordPolicy', 'whiteSpaces'].includes(passwordResetErrorKey) ? t(`users.resetPasswordErrors.${passwordResetErrorKey}`) : null}
        rules={[
          { required: true, message: t('users.passwordRequired') },
          () => ({ validator (rule, value) { return validatePasswordPolicy(value); } })
        ]}
      >
        <Row justify='space-between' align='middle'>
          <Col span={18}>
            <LrvPasswordV2
              label={
                <span className={styles.label}>
                  {t('users.newUserPassword', { name: selectedUser.firstName + ' ' + selectedUser.lastName })}&nbsp;
                  <PasswordPolicy />
                </span>
              }
              theme='light'
              id='newPassword'
              autoComplete='off'
              value={userTargetNewPassword}
              hasError={hasError}
              onChange={(e) => setUserTargetNewPassword(e.target.value)}
            />
          </Col>
          {renderGeneratePasswordButton()}
        </Row>
      </Form.Item>
    );
  };

  return (
    <LrvModal
      theme='light'
      className={styles.resetPasswordModal}
      title={t('users.resetPassword')}
      open={showResetPasswordModal}
      destroyOnClose={true}
      okButtonProps={{ id: 'submit_reset_password', disabled: disabledButtonFormResetPassword, htmlType: 'submit', form: 'formResetPassword', loading: isResettingPassword }}
      onOk={() => {
        dispatch(usersSlice.resetUserPassword(selectedUser._id, userSessionPassword, userTargetNewPassword));
      }}
      okText={t('users.save')}
      cancelText={t('users.cancel')}
      closeIcon={<Icon id='close_reset_password_modal' name='close' />}
      cancelButtonProps={{ id: 'cancel_reset_password' }}
      onCancel={() => {
        dispatch(usersSlice.setShowResetPasswordModal(false));
        form.setFieldsValue({
          currentPassword: undefined,
          newPassword: undefined,
        });
        dispatch(usersSlice.setIsResettingPassword(false));
        dispatch(usersSlice.setHasNewPasswordError(false));
        dispatch(usersSlice.setHasCurrentPasswordError(false));
        dispatch(usersSlice.setPasswordResetErrorKey(''));
        setDisabledButtonFormResetPassword(true);
        setUserSessionPassword('');
        setUserTargetNewPassword('');
      }}
    >
      <LrvForm
        form={form}
        theme='light'
        name='formResetPassword'
        id='formResetPassword'
        layout='vertical'
        onFieldsChange={() => {
          setDisabledButtonFormResetPassword(
            (userTargetNewPassword === '' || userSessionPassword === '') || form.getFieldsError().filter(({ errors }) => errors.length).length > 0
          );

          extractAndSetErrorFields();
        }}
      >
        {renderCurrentPassword()}
        {renderNewPassword()}
      </LrvForm>
    </LrvModal>
  );
};
